(() => {
  const availabilityParameter = 'available_only';
  const pageCache = new Map();
  let activeRun = 0;

  const availabilityOnlyIsActive = () => new URL(window.location.href).searchParams.get(availabilityParameter) === '1';

  const syncAvailabilityInputs = () => {
    const isActive = availabilityOnlyIsActive();
    document.querySelectorAll('.thyrana-availability-filter__input').forEach((input) => {
      input.checked = isActive;
    });
  };

  const itemIsAvailable = (item) => item.dataset.productAvailable === 'true';

  const orderItems = (items) => {
    const availableItems = items.filter(itemIsAvailable);
    const soldOutItems = items.filter((item) => !itemIsAvailable(item));
    return { availableItems, orderedItems: [...availableItems, ...soldOutItems] };
  };

  const updateProductCount = (count) => {
    const label = `${count} ${count === 1 ? 'Produkt' : 'Produkte'}`;
    document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach((element) => {
      element.textContent = label;
    });
  };

  const getPaginationUrl = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.set(availabilityParameter, '1');
    if (page === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }
    return url.toString();
  };

  const renderAvailabilityPagination = (container, totalPages, currentPage) => {
    let wrapper = container?.querySelector('.pagination-wrapper');

    if (totalPages <= 1) {
      wrapper?.remove();
      return;
    }

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'pagination-wrapper';
      container?.querySelector('.collection')?.append(wrapper);
    }

    const navigation = document.createElement('nav');
    navigation.className = 'pagination';
    navigation.setAttribute('role', 'navigation');
    navigation.setAttribute('aria-label', 'Seitennummerierung');
    const list = document.createElement('ul');
    list.className = 'pagination__list list-unstyled';
    list.setAttribute('role', 'list');

    const addLink = (label, page, options = {}) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = `pagination__item link${options.current ? ' pagination__item--current light' : ''}${
        options.arrow ? ' pagination__item-arrow motion-reduce' : ''
      }`;
      link.href = getPaginationUrl(page);
      link.textContent = label;
      link.setAttribute('aria-label', options.ariaLabel || `Seite ${page}`);
      if (options.current) link.setAttribute('aria-current', 'page');
      item.append(link);
      list.append(item);
    };

    if (currentPage > 1) addLink('‹', currentPage - 1, { arrow: true, ariaLabel: 'Vorherige Seite' });

    const visiblePages = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])]
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    visiblePages.forEach((page, index) => {
      if (index > 0 && page - visiblePages[index - 1] > 1) {
        const item = document.createElement('li');
        const separator = document.createElement('span');
        separator.className = 'pagination__item';
        separator.textContent = '…';
        separator.setAttribute('aria-hidden', 'true');
        item.append(separator);
        list.append(item);
      }
      addLink(String(page), page, { current: page === currentPage });
    });

    if (currentPage < totalPages) addLink('›', currentPage + 1, { arrow: true, ariaLabel: 'Nächste Seite' });

    navigation.append(list);
    wrapper.replaceChildren(navigation);
  };

  const getPageUrl = (page, sectionId) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(availabilityParameter);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    return url.toString();
  };

  const fetchPageItems = async (page, sectionId) => {
    const url = getPageUrl(page, sectionId);
    let html = pageCache.get(url);

    if (!html) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Produktseite ${page} konnte nicht geladen werden.`);
      html = await response.text();
      pageCache.set(url, html);
    }

    const parsedGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
    if (!parsedGrid) throw new Error(`Produktliste ${page} fehlt.`);
    return [...parsedGrid.children];
  };

  const prepareItems = (items) => {
    items.forEach((item) => {
      item.classList.add('scroll-trigger--cancel');
      item.removeAttribute('style');
    });
    return items;
  };

  const organizeProducts = async () => {
    syncAvailabilityInputs();

    const grid = document.querySelector('#product-grid[data-thyrana-availability-sort]');
    if (!grid) return;

    const run = ++activeRun;
    const container = grid.closest('.product-grid-container');
    const totalPages = Number.parseInt(grid.dataset.thyranaTotalPages || '1', 10);
    const currentPage = Number.parseInt(grid.dataset.thyranaCurrentPage || '1', 10);
    const pageSize = Number.parseInt(grid.dataset.thyranaPageSize || String(grid.children.length), 10);
    const sectionId = grid.dataset.thyranaSectionId;
    const currentItems = [...grid.children];
    const currentOrder = orderItems(currentItems);

    grid.replaceChildren(...prepareItems(currentOrder.orderedItems));
    grid.setAttribute('aria-busy', 'true');
    container?.classList.add('thyrana-grid-is-organizing');

    if (!sectionId || totalPages <= 1) {
      const visibleItems = availabilityOnlyIsActive() ? currentOrder.availableItems : currentOrder.orderedItems;
      grid.replaceChildren(...prepareItems(visibleItems));
      if (availabilityOnlyIsActive()) {
        updateProductCount(visibleItems.length);
        renderAvailabilityPagination(container, 1, 1);
      }
      grid.removeAttribute('aria-busy');
      container?.classList.remove('thyrana-grid-is-organizing');
      return;
    }

    try {
      const pages = new Array(totalPages);
      pages[currentPage - 1] = currentItems;
      const pagesToLoad = [];

      for (let page = 1; page <= totalPages; page += 1) {
        if (page !== currentPage) pagesToLoad.push(page);
      }

      for (let offset = 0; offset < pagesToLoad.length; offset += 4) {
        const batch = pagesToLoad.slice(offset, offset + 4);
        const results = await Promise.all(batch.map((page) => fetchPageItems(page, sectionId)));
        batch.forEach((page, index) => {
          pages[page - 1] = results[index];
        });
        if (run !== activeRun) return;
      }

      const allItems = pages.flat();
      const { availableItems, orderedItems } = orderItems(allItems);
      const onlyAvailable = availabilityOnlyIsActive();
      let visibleItems;

      if (onlyAvailable) {
        const availablePages = Math.max(1, Math.ceil(availableItems.length / pageSize));
        const availablePage = Math.min(currentPage, availablePages);
        const start = (availablePage - 1) * pageSize;
        visibleItems = availableItems.slice(start, start + pageSize);
        updateProductCount(availableItems.length);
        renderAvailabilityPagination(container, availablePages, availablePage);
      } else {
        const start = (currentPage - 1) * pageSize;
        visibleItems = orderedItems.slice(start, start + pageSize);
      }

      if (run !== activeRun) return;
      grid.replaceChildren(...prepareItems(visibleItems));
    } catch (error) {
      console.warn('[Thyrana] Verfügbarkeits-Sortierung konnte nicht vollständig geladen werden.', error);
    } finally {
      if (run === activeRun) {
        grid.removeAttribute('aria-busy');
        container?.classList.remove('thyrana-grid-is-organizing');
      }
    }
  };

  document.addEventListener('DOMContentLoaded', organizeProducts);
  document.addEventListener('thyrana:product-grid-updated', organizeProducts);
  window.addEventListener('popstate', syncAvailabilityInputs);
})();
